/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Request builders for the Univen attributes (plan §3.1, §5.4).
 *
 * Two write modes, because the backend treats them differently:
 * - create (no `initial`): an empty field is left out. POST rejects a null
 *   in a typed field, and absence already means "not informed".
 * - update (`initial` = the values the form opened with): a field the user
 *   emptied is sent as `null`, which is how PUT clears it. A field that was
 *   empty before and still is stays out, so an unrelated save never writes
 *   a pile of nulls into storage.
 *
 * Pricing blocks (`short_term`, `auction`) go whole, carrying every field the
 * form holds, so a deep merge on the server can never keep a stale sibling.
 */

import type { PropertyFormValues } from './schema';
import { AUCTION_TRANSACTION, SHORT_TERM_TRANSACTION } from './schema.constants';

/** The values the form opened with; present only when updating. */
export interface AttributeWriteOptions {
  initial?: Partial<PropertyFormValues> | null;
}

type FormKey = keyof PropertyFormValues;
type Block = Record<string, unknown>;

/** Field map of one request block: request key -> form key. */
type FieldMap = ReadonlyArray<readonly [string, FormKey]>;

const LOCATION_FIELDS: FieldMap = [
  ['distance_to_sea_m', 'distance_to_sea_m'],
  ['block', 'block'],
  ['unit', 'unit'],
  ['quadra', 'quadra'],
  ['lot', 'lot'],
];

const CHARACTERISTICS_FIELDS: FieldMap = [
  ['condition', 'condition'],
  ['occupancy', 'occupancy'],
  ['finish_standard', 'finish_standard'],
  ['location_quality', 'location_quality'],
];

const CONDOMINIUM_FIELDS: FieldMap = [
  ['name', 'condominium_name'],
  ['development_name', 'development_name'],
  ['developer', 'developer'],
];

const CATEGORY_SPECIFICS_FIELDS: FieldMap = [['tower_name', 'tower_name']];

const OWNERSHIP_TEXT_FIELDS: FieldMap = [
  ['exclusive_until', 'exclusive_until'],
  ['publish_authorized_until', 'publish_authorized_until'],
  ['key_location', 'key_location'],
  ['partner_name', 'partner_name'],
];

const OWNERSHIP_FLAG_FIELDS: FieldMap = [
  ['exclusive', 'exclusive'],
  ['publish_authorized', 'publish_authorized'],
];

const SHORT_TERM_FIELDS: FieldMap = [
  ['price', 'short_term_price'],
  ['guests', 'short_term_guests'],
  ['cleaning_fee', 'short_term_cleaning_fee'],
];

const AUCTION_FIELDS: FieldMap = [
  ['first_date', 'auction_first_date'],
  ['first_price', 'auction_first_price'],
  ['second_date', 'auction_second_date'],
  ['second_price', 'auction_second_price'],
];

/** Pricing figures where zero means "not informed" (it never marks a transaction). */
const ZERO_IS_EMPTY = new Set<FormKey>([
  'short_term_price', 'short_term_guests', 'short_term_cleaning_fee',
  'auction_first_price', 'auction_second_price',
]);

/**
 * Whether a form value counts as not informed.
 *
 * @param key - The form field, to know whether zero is empty.
 * @param value - The value as held by the form.
 * @returns True for undefined, null, blank text, NaN, and zero where zero means nothing.
 */
export function isEmptyAttribute(key: FormKey, value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return typeof value === 'number' && isEmptyNumber(key, value);
}

/**
 * Whether a number counts as not informed.
 *
 * @param key - The form field, to know whether zero is empty.
 * @param value - The number.
 * @returns True for NaN, and for zero on the pricing figures.
 */
function isEmptyNumber(key: FormKey, value: number): boolean {
  return Number.isNaN(value) || (value === 0 && ZERO_IS_EMPTY.has(key));
}

/**
 * Converts a tri-state select value to the stored boolean.
 *
 * @param value - 'yes', 'no' or empty.
 * @returns true / false, or undefined when not informed.
 */
function toFlag(value: unknown): boolean | undefined {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
}

/**
 * Trims text so a stray space is not stored as a different spelling.
 *
 * @param value - The value as held by the form.
 * @returns The trimmed text, or the value untouched when it is not text.
 */
function clean(value: unknown): unknown {
  return typeof value === 'string' ? value.trim() : value;
}

/**
 * Builds one block from a field map, honouring the create/update rules.
 *
 * @param values - Current form values.
 * @param fields - Request key -> form key pairs.
 * @param options - Update mode when `initial` is given.
 * @param convert - Optional conversion of the form value to the stored one.
 * @returns The block, empty when nothing is to be sent.
 */
function buildFields(
  values: Partial<PropertyFormValues>,
  fields: FieldMap,
  options: AttributeWriteOptions,
  convert: (value: unknown) => unknown = clean,
): Block {
  const block: Block = {};
  for (const [requestKey, formKey] of fields) {
    const value = values[formKey];
    if (!isEmptyAttribute(formKey, value)) {
      block[requestKey] = convert(value);
    } else if (wasInformed(options, formKey)) {
      block[requestKey] = null;
    }
  }
  return block;
}

/**
 * Whether the form opened with this field informed (update mode only).
 *
 * @param options - Write options.
 * @param formKey - The form field.
 * @returns True when updating and the stored value was not empty.
 */
function wasInformed(options: AttributeWriteOptions, formKey: FormKey): boolean {
  const initial = options.initial;
  if (!initial) return false;
  return !isEmptyAttribute(formKey, initial[formKey]);
}

/**
 * Adds `extra` to `target` in place and returns whether anything was added.
 *
 * @param target - The block being populated.
 * @param extra - The keys to add.
 * @returns The populated target.
 */
function mergeInto<T extends object>(target: T | undefined, extra: Block): T | undefined {
  if (Object.keys(extra).length === 0) return target;
  return { ...(target ?? {}), ...extra } as T;
}

/**
 * Location extras: distance to the sea, block, unit, quadra and lot.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns The keys to merge into `location`.
 */
export function buildLocationAttributes(values: Partial<PropertyFormValues>, options: AttributeWriteOptions = {}): Block {
  return buildFields(values, LOCATION_FIELDS, options);
}

/**
 * Condition, occupancy, finish standard and location quality.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns The keys to merge into `characteristics`.
 */
export function buildCharacteristicsAttributes(values: Partial<PropertyFormValues>, options: AttributeWriteOptions = {}): Block {
  return buildFields(values, CHARACTERISTICS_FIELDS, options);
}

/**
 * Condominium name, development and developer.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns The keys to merge into `condominium`.
 */
export function buildCondominiumAttributes(values: Partial<PropertyFormValues>, options: AttributeWriteOptions = {}): Block {
  return buildFields(values, CONDOMINIUM_FIELDS, options);
}

/**
 * Exclusivity, publishing authorization, key location and partner.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns The keys to merge into `ownership`.
 */
export function buildOwnershipAttributes(values: Partial<PropertyFormValues>, options: AttributeWriteOptions = {}): Block {
  // A deadline only means something while its answer is yes; the form hides
  // it otherwise, and a hidden date must not be saved (or must be cleared).
  const effective = {
    ...values,
    exclusive_until: values.exclusive === 'yes' ? values.exclusive_until : undefined,
    publish_authorized_until: values.publish_authorized === 'yes' ? values.publish_authorized_until : undefined,
  };
  return {
    ...buildFields(effective, OWNERSHIP_FLAG_FIELDS, options, toFlag),
    ...buildFields(effective, OWNERSHIP_TEXT_FIELDS, options),
  };
}

/**
 * Builds a whole pricing block (§5.4) for one transaction.
 *
 * Offered: every informed field, plus nulls for the ones the user emptied.
 * Not offered (chip off): nothing on create; on update the block the
 * property held is cleared, or the server would put the transaction back.
 *
 * @param values - Current form values.
 * @param fields - The block's field map.
 * @param offered - Whether the transaction chip is on.
 * @param options - Update mode when `initial` is given.
 * @returns The block, `null` to clear it, or undefined to leave it out.
 */
function buildPricingBlock(
  values: Partial<PropertyFormValues>,
  fields: FieldMap,
  offered: boolean,
  options: AttributeWriteOptions,
): Block | null | undefined {
  const heldBefore = fields.some(([, formKey]) => wasInformed(options, formKey));
  const block = offered ? buildFields(values, fields, options) : {};
  const informed = Object.values(block).some((v) => v !== null);
  if (informed) return block;
  return heldBefore ? null : undefined;
}

/**
 * The Temporada and Leilão pricing blocks.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns `short_term` / `auction` entries to add to `pricing` (absent when not sent).
 */
export function buildPricingAttributes(
  values: Partial<PropertyFormValues>,
  options: AttributeWriteOptions = {},
): { short_term?: Block | null; auction?: Block | null } {
  const types = values.transaction_type ?? [];
  const shortTerm = buildPricingBlock(values, SHORT_TERM_FIELDS, types.includes(SHORT_TERM_TRANSACTION), options);
  const auction = buildPricingBlock(values, AUCTION_FIELDS, types.includes(AUCTION_TRANSACTION), options);
  const result: { short_term?: Block | null; auction?: Block | null } = {};
  if (shortTerm !== undefined) result.short_term = shortTerm;
  if (auction !== undefined) result.auction = auction;
  return result;
}

/**
 * The building / tower name, kept in `category_specifics`.
 *
 * @param values - Current form values.
 * @param options - Update mode when `initial` is given.
 * @returns The keys to merge into `category_specifics`.
 */
export function buildCategorySpecificsAttributes(values: Partial<PropertyFormValues>, options: AttributeWriteOptions = {}): Block {
  return buildFields(values, CATEGORY_SPECIFICS_FIELDS, options);
}

/** Request blocks the attribute builders write into. */
export interface AttributeTarget {
  location?: object;
  characteristics?: object;
  condominium?: object;
  ownership?: object;
  pricing?: object;
  category_specifics?: object;
}

/**
 * Applies every Univen attribute to a request being built, in place.
 *
 * The condominium extras and the tower name go only with the condominium
 * block, which the form sends only while "Imóvel em condomínio" is on.
 *
 * @param values - Current form values.
 * @param request - The nested request body.
 * @param options - Update mode when `initial` is given.
 */
export function applyAttributes(
  values: Partial<PropertyFormValues>,
  request: AttributeTarget,
  options: AttributeWriteOptions = {},
): void {
  request.location = mergeInto(request.location, buildLocationAttributes(values, options));
  request.characteristics = mergeInto(request.characteristics, buildCharacteristicsAttributes(values, options));
  request.ownership = mergeInto(request.ownership, buildOwnershipAttributes(values, options));
  request.pricing = mergeInto(request.pricing, buildPricingAttributes(values, options));
  if (!values.condominium) return;
  request.condominium = mergeInto(request.condominium, buildCondominiumAttributes(values, options));
  request.category_specifics = mergeInto(request.category_specifics, buildCategorySpecificsAttributes(values, options));
}
